import type { AxiosError } from 'axios';
import { DestinationErrorType } from '../types';
import { upperFirst } from 'lodash-es';
const typedEntries = <T extends Record<string, unknown>>(object: T) =>
  Object.entries(object) as [keyof T & string, T[keyof T]][];

const ErrorGroup = {
  SUCCESS: 'Success',
  RETRY: 'Retry',
  DISCARDED: 'Discarded',
  PARTIAL_SUCCESS: 'Partial Success',
  OTHER: 'Other',
} as const;
type ErrorGroup = (typeof ErrorGroup)[keyof typeof ErrorGroup];

const LABELS: Record<DestinationErrorType, string> = {
  [DestinationErrorType.ERRORS_RETRY_UNKNOWN]: 'Unknown network failure',
  [DestinationErrorType.ERRORS_DISCARDED_400]: '400 Bad Request',
  [DestinationErrorType.ERRORS_DISCARDED_401]: '401 Unauthorized',
  [DestinationErrorType.ERRORS_DISCARDED_403]: '403 Forbidden',
  [DestinationErrorType.ERRORS_DISCARDED_INVALID_SETTINGS]: "Invalid destination's settings",
  [DestinationErrorType.ERRORS_DISCARDED_MESSAGE_REJECTED]: 'Message rejected by destination',
  [DestinationErrorType.ERROR_RETRY_429]: '429 Too many requests',
  [DestinationErrorType.ERROR_RETRY_5XX]: '500 Internal Server Error',
  [DestinationErrorType.ERROR_RETRY_NETWORK]: 'Network failure',
  [DestinationErrorType.ERROR_OTHER]: 'Uncategorized error',
  [DestinationErrorType.ERROR_NO_MATCHING_DESTINATION]: 'Destination has no matching action',
  [DestinationErrorType.NO_ERROR]: 'Successful',
  [DestinationErrorType.ERROR_PARTIAL_SUCCESS_207]: '207 Partial Success',
};

const ERROR_TYPE_TO_GROUP: Record<DestinationErrorType, ErrorGroup> = {
  [DestinationErrorType.NO_ERROR]: ErrorGroup.SUCCESS,

  // Retry errors
  [DestinationErrorType.ERRORS_RETRY_UNKNOWN]: ErrorGroup.RETRY,
  [DestinationErrorType.ERROR_RETRY_429]: ErrorGroup.RETRY,
  [DestinationErrorType.ERROR_RETRY_5XX]: ErrorGroup.RETRY,
  [DestinationErrorType.ERROR_RETRY_NETWORK]: ErrorGroup.RETRY,

  // Discarded errors
  [DestinationErrorType.ERRORS_DISCARDED_400]: ErrorGroup.DISCARDED,
  [DestinationErrorType.ERRORS_DISCARDED_401]: ErrorGroup.DISCARDED,
  [DestinationErrorType.ERRORS_DISCARDED_403]: ErrorGroup.DISCARDED,
  [DestinationErrorType.ERRORS_DISCARDED_INVALID_SETTINGS]: ErrorGroup.DISCARDED,
  [DestinationErrorType.ERRORS_DISCARDED_MESSAGE_REJECTED]: ErrorGroup.DISCARDED,

  // Partial success - some items in a batch succeeded, others failed.
  // This is distinct from DISCARDED because some data was successfully delivered.
  [DestinationErrorType.ERROR_PARTIAL_SUCCESS_207]: ErrorGroup.PARTIAL_SUCCESS,

  // Other errors
  [DestinationErrorType.ERROR_OTHER]: ErrorGroup.OTHER,
  [DestinationErrorType.ERROR_NO_MATCHING_DESTINATION]: ErrorGroup.OTHER,
};

// Map string enum values to numeric values that match the backend's proto definition
const ERROR_TYPE_TO_NUMBER: Record<string, number> = {
  [DestinationErrorType.ERRORS_RETRY_UNKNOWN]: 0,
  [DestinationErrorType.ERRORS_DISCARDED_400]: 1,
  [DestinationErrorType.ERRORS_DISCARDED_401]: 2,
  [DestinationErrorType.ERRORS_DISCARDED_403]: 3,
  [DestinationErrorType.ERRORS_DISCARDED_INVALID_SETTINGS]: 4,
  [DestinationErrorType.ERRORS_DISCARDED_MESSAGE_REJECTED]: 5,
  [DestinationErrorType.ERROR_RETRY_429]: 6,
  [DestinationErrorType.ERROR_RETRY_5XX]: 7,
  [DestinationErrorType.ERROR_RETRY_NETWORK]: 8,
  [DestinationErrorType.ERROR_OTHER]: 9,
  [DestinationErrorType.ERROR_NO_MATCHING_DESTINATION]: 10,
  [DestinationErrorType.NO_ERROR]: 11,
  [DestinationErrorType.ERROR_PARTIAL_SUCCESS_207]: 12,
};

export const NO_MATCHING_DESTINATION_CODE =
  ERROR_TYPE_TO_NUMBER[DestinationErrorType.ERROR_NO_MATCHING_DESTINATION].toString();

export const ERROR_LABEL_OPTIONS = [
  ...typedEntries(LABELS)
    .filter(([value]) => ERROR_TYPE_TO_GROUP[value] === ErrorGroup.SUCCESS)
    .map(([value, label]) => ({
      value: ERROR_TYPE_TO_NUMBER[value],
      label,
      group: ERROR_TYPE_TO_GROUP[value],
    })),

  ...typedEntries(LABELS)
    .filter(
      ([value]) =>
        value === ERROR_TYPE_TO_NUMBER[DestinationErrorType.NO_ERROR].toString() ||
        value === ERROR_TYPE_TO_NUMBER[DestinationErrorType.ERROR_NO_MATCHING_DESTINATION].toString(),
    )
    .map(([value, label]) => ({
      value: ERROR_TYPE_TO_NUMBER[value],
      label,
      group: ERROR_TYPE_TO_GROUP[value],
    })),

  // Partial success
  ...typedEntries(LABELS)
    .filter(([value]) => ERROR_TYPE_TO_GROUP[value] === ErrorGroup.PARTIAL_SUCCESS)
    .map(([value, label]) => ({
      value: ERROR_TYPE_TO_NUMBER[value],
      label,
      group: ERROR_TYPE_TO_GROUP[value],
    })),

  // Second group - Retryable errors
  ...typedEntries(LABELS)
    .filter(([value]) => ERROR_TYPE_TO_GROUP[value] === ErrorGroup.RETRY)
    .map(([value, label]) => ({
      value: ERROR_TYPE_TO_NUMBER[value],
      label,
      group: ERROR_TYPE_TO_GROUP[value],
    })),

  // Third group - Discarded errors
  ...typedEntries(LABELS)
    .filter(([value]) => ERROR_TYPE_TO_GROUP[value] === ErrorGroup.DISCARDED)
    .map(([value, label]) => ({
      value: ERROR_TYPE_TO_NUMBER[value],
      label,
      group: ERROR_TYPE_TO_GROUP[value],
    })),

  // Fourth group - Other errors
  ...typedEntries(LABELS)
    .filter(
      ([value]) =>
        ERROR_TYPE_TO_GROUP[value] === ErrorGroup.OTHER && value !== DestinationErrorType.ERROR_NO_MATCHING_DESTINATION,
    )
    .map(([value, label]) => ({
      value: ERROR_TYPE_TO_NUMBER[value],
      label,
      group: ERROR_TYPE_TO_GROUP[value],
    })),
];

export const getDestinationErrorType = (statusCode: number, errorMessage: string): DestinationErrorType | null => {
  // Return null for successful status codes
  if (statusCode === 200 || statusCode === 201 || statusCode === 202 || statusCode === 204) {
    return null;
  }

  if (errorMessage === 'destination has no matching action') {
    return DestinationErrorType.ERROR_NO_MATCHING_DESTINATION; // 10
  }

  if (statusCode === 207) {
    return DestinationErrorType.ERROR_PARTIAL_SUCCESS_207; // 12
  }

  if (statusCode === 400) {
    return DestinationErrorType.ERRORS_DISCARDED_400; // 1
  }

  if (statusCode === 401) {
    return DestinationErrorType.ERRORS_DISCARDED_401; // 2
  }

  if (statusCode === 403) {
    return DestinationErrorType.ERRORS_DISCARDED_403; // 3
  }

  if (statusCode === 429) {
    return DestinationErrorType.ERROR_RETRY_429; // 6
  }

  if (statusCode !== 0 && statusCode >= 500 && statusCode < 600) {
    return DestinationErrorType.ERROR_RETRY_5XX; // 7
  }

  return DestinationErrorType.ERROR_OTHER; // 9
};

export const labelForDestinationError = (type: DestinationErrorType) => LABELS[type];

export const extractErrorsIfBadRequest = (error: AxiosError) => {
  if (error.response?.status === 400) {
    const data = error.response?.data;

    if (data == null) {
      return [];
    }

    const datas = Array.isArray(data) ? data : [data];
    const contentType = error.response.headers?.['content-type'];
    const isJson = typeof contentType === 'string' && contentType.includes('application/json');

    if (isJson) {
      return datas.map((d) => (upperFirst(d.message) !== '' ? upperFirst(d.message) : JSON.stringify(d)));
    }

    return datas;
  }
  return [];
};
